import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { BatchLinkCards } from "@/components/batch/batch-link-cards";
import { ModernBatchHeader } from "@/components/batch/modern-batch-header";
import type { BatchExamCount } from "@/lib/batch-helpers";
import { pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(pages)/batches/[batchId]/page.tsx + batch-page.tsx.
 * Teachers query is intentionally lazy (post-mount) — JSON-contains scan on
 * teacher.batches dominates p95; portal-ui has the same pattern.
 */
interface BatchRecord {
  id: string;
  name: string;
  exam_year?: string;
  start_date?: string;
}

interface TeacherRecord {
  id: string;
  name: string;
  subjects: string[];
  qualifications?: string;
  batches: string[];
  login?: string;
}

function batchQuery(batchId: string, authId: string | undefined) {
  return {
    queryKey: ["batch", batchId, authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("batch").getOne<BatchRecord>(batchId, {
        fields: "id,name,exam_year,start_date",
      }),
  };
}

function batchExamCountQuery(batchId: string, authId: string | undefined) {
  return {
    queryKey: ["batch", batchId, "exam-count", authId ?? "anon"] as const,
    queryFn: () =>
      pb
        .collection("batch_exam_count")
        .getList<BatchExamCount>(1, 10, { filter: `batch = '${batchId}'` }),
  };
}

function batchStudentTotalQuery(batchId: string, authId: string | undefined) {
  return {
    queryKey: ["batch", batchId, "student-total", authId ?? "anon"] as const,
    // Only `totalItems` is consumed — project to `id` to keep the per-row
    // payload tiny.
    queryFn: () =>
      pb.collection("student").getList(1, 1, {
        filter: `batch = '${batchId}'`,
        fields: "id",
      }),
  };
}

export const Route = createFileRoute("/_app/batches/$batchId")({
  loader: ({ context, params }) => {
    const authId = pb.authStore.model?.id;
    return Promise.all([
      context.queryClient.ensureQueryData(batchQuery(params.batchId, authId)),
      context.queryClient.ensureQueryData(batchExamCountQuery(params.batchId, authId)),
      context.queryClient.ensureQueryData(batchStudentTotalQuery(params.batchId, authId)),
    ]);
  },
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { batchId } = Route.useParams();
  const authId = pb.authStore.model?.id;

  const { data: batch } = useSuspenseQuery(batchQuery(batchId, authId));
  const { data: examCountList } = useSuspenseQuery(batchExamCountQuery(batchId, authId));
  const { data: studentTotal } = useSuspenseQuery(batchStudentTotalQuery(batchId, authId));

  const [teachers, setTeachers] = useState<TeacherRecord[]>([]);
  useEffect(() => {
    if (!batch.id) return;
    let cancelled = false;
    pb.collection("teacher")
      .getList<TeacherRecord>(1, 10, {
        filter: `batches.id ?= '${batch.id}'`,
        skipTotal: true,
      })
      .then((page) => {
        if (!cancelled) setTeachers(page.items);
      })
      .catch(() => {
        /* header copes with empty list */
      });
    return () => {
      cancelled = true;
    };
  }, [batch.id]);

  useDocumentTitle(batch.name);

  const examCount = examCountList.items.reduce((sum, x) => sum + (x.nof_exams ?? 0), 0);

  return (
    <div className="flex flex-col gap-6">
      <ModernBatchHeader
        batch={batch}
        teachers={teachers}
        totalStudents={studentTotal.totalItems}
        examCount={examCount}
        batchExamCount={examCountList.items}
      />
      <BatchLinkCards
        batchId={batch.id}
        batchName={batch.name}
        examCount={examCount}
        studentCount={studentTotal.totalItems}
      />
    </div>
  );
}
